/**
 * NotificationChannelsList Component
 *
 * Displays a list of notification channels with:
 * - Channel name and type
 * - Status and configuration
 * - Test, edit, and delete actions
 *
 * Migrated onto the List & Grid Standard local primitives (`src/components/list/`) — see
 * `.specify/journals/list-grid-adoption-journal.md` and the reference migration
 * `src/pages/ApiKeysPage.tsx`. This is a CHILD component embedded in `IntegrationsPage`, so it
 * uses `ListFrame` (not `ListPageLayout`) and keeps its existing `Card`/`CardHeader` shell.
 */
import { useEffect, useMemo, useState } from 'react';
import type { FC, ReactNode } from 'react';
import {
  Card,
  CardHeader,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
  Button,
  Spinner,
  Text,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  tokens,
  makeStyles,
  Tooltip,
} from '@fluentui/react-components';
import {
  AlertRegular,
  DeleteRegular,
  ArrowSyncRegular,
  CheckmarkCircleRegular,
  ErrorCircleRegular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import {
  useNotificationChannels,
  useTestNotificationChannel,
  useDeleteNotificationChannel,
} from '../hooks';
import type { NotificationChannel, NotificationChannelType, NotificationChannelStatus, ConnectionTestResult } from '../types';
import { useAuth } from '../../authentication';
import {
  ListFrame,
  Pager,
  PAGE_SIZE,
  actionsColumnWidth,
  useListUrlState,
  makeSortComparator,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../../../components/list';
import type { SortDirection, SortState } from '../../../components/list';

/**
 * Roles allowed to delete a notification channel — mirrors `NotificationController.DeleteChannel`
 * (`RequireAnyRole("Organization.Admin", "TenantAdmin", "IntegrationAdmin")`, also used by
 * create/update/retry). `TestChannel` carries NO role check server-side, so the Test action stays
 * visible to every user regardless of role — only Delete is hidden-not-disabled here.
 */
const NOTIFICATION_CHANNEL_MANAGER_ROLES = ['Organization.Admin', 'TenantAdmin', 'IntegrationAdmin'];

const useStyles = makeStyles({
  card: {
    width: '100%',
  },
  listSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalM,
  },
  headerActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  channelTypeIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  stats: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    fontSize: tokens.fontSizeBase200,
  },
  successCount: {
    color: tokens.colorPaletteGreenForeground1,
  },
  failureCount: {
    color: tokens.colorPaletteRedForeground1,
  },
  actionsCell: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
  },
});

/**
 * Get badge color based on channel status
 */
function getStatusBadgeColor(status: NotificationChannelStatus): 'success' | 'informative' | 'danger' | 'subtle' {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Configured':
      return 'informative';
    case 'Error':
      return 'danger';
    case 'Disabled':
    default:
      return 'subtle';
  }
}

/**
 * Get channel type display info
 */
function getChannelTypeInfo(type: NotificationChannelType): { label: string; color: 'brand' | 'informative' | 'warning' | 'subtle' } {
  switch (type) {
    case 'Slack':
      return { label: 'Slack', color: 'warning' };
    case 'MicrosoftTeams':
      return { label: 'Teams', color: 'brand' };
    case 'Webhook':
      return { label: 'Webhook', color: 'informative' };
    case 'Email':
      return { label: 'Email', color: 'subtle' };
    default:
      return { label: type, color: 'subtle' };
  }
}

/**
 * Format a date for display
 */
function formatDate(dateString?: string): string {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleString();
}

export interface NotificationChannelsListProps {
  onAddChannel?: () => void;
  /** Custom content to render in the empty state */
  emptyStateContent?: ReactNode;
}

export const NotificationChannelsList: FC<NotificationChannelsListProps> = ({
  onAddChannel,
  emptyStateContent,
}) => {
  const styles = useStyles();
  const { user } = useAuth();
  const canManageChannels = user?.roles?.some((role) => NOTIFICATION_CHANNEL_MANAGER_ROLES.includes(role)) ?? false;

  const { data: channels, isLoading, isFetching, isError, error, refetch } = useNotificationChannels();
  const testChannel = useTestNotificationChannel();
  const deleteChannel = useDeleteNotificationChannel();

  const [channelToDelete, setChannelToDelete] = useState<NotificationChannel | null>(null);
  const [testingChannelId, setTestingChannelId] = useState<number | null>(null);
  const [testResult, setTestResult] = useState<{ id: number; result: ConnectionTestResult } | null>(null);

  // No server-paged endpoint exists for channels (`GET /api/portal/notifications/channels` returns
  // the full unpaged array) — `useListUrlState` drives page + sort and pagination/sorting run
  // client-side over the hook's data, per PAGE_SIZE=25.
  const { state, setPage, setSort } = useListUrlState({ defaultSort: 'name:asc' });

  const [sortField, sortDirRaw] = state.sort!.split(':');
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';
  const sortState: SortState[] = [{ field: sortField, dir: sortDir }];

  const toggleSort = (field: string) => {
    if (field === sortField) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const sortIcon = (field: string) => {
    if (field !== sortField) return <ArrowSort16Regular />;
    return sortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const rawChannels = useMemo(() => channels ?? [], [channels]);
  const sortedChannels = useMemo(
    () => [...rawChannels].sort(makeSortComparator(sortState)),
    [rawChannels, sortState]
  );

  const totalCount = sortedChannels.length;
  const totalPages = Math.max(Math.ceil(totalCount / PAGE_SIZE), 1);
  const currentPage = Math.min(state.page, totalPages);
  const hasPreviousPage = currentPage > 1;
  const hasNextPage = currentPage < totalPages;

  const pagedChannels = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedChannels.slice(start, start + PAGE_SIZE);
  }, [sortedChannels, currentPage]);

  // Out-of-range clamp (§9) — never strand the user on an empty page after a delete.
  useEffect(() => {
    if (!isLoading && state.page > totalPages) {
      setPage(totalPages);
    }
  }, [isLoading, state.page, totalPages, setPage]);

  const handleTestChannel = async (channel: NotificationChannel) => {
    setTestingChannelId(channel.id);
    try {
      const result = await testChannel.mutateAsync(channel.id);
      setTestResult({ id: channel.id, result });
      setTimeout(() => setTestResult(null), 5000);
    } finally {
      setTestingChannelId(null);
    }
  };

  const handleDeleteChannel = async () => {
    if (channelToDelete) {
      await deleteChannel.mutateAsync(channelToDelete.id);
      setChannelToDelete(null);
    }
  };

  // Test is always available (no server-side role gate on `POST .../test`); Delete is
  // hidden-not-disabled for non-managers, so the Actions column is never fully omitted.
  const actionButtonCount = canManageChannels ? 2 : 1;

  let listBody: ReactNode;
  if (isLoading) {
    listBody = <LoadingSkeleton rows={6} columns={7} />;
  } else if (isError) {
    listBody = (
      <ErrorState message={error?.message ?? 'Unable to load notification channels.'} onRetry={() => refetch()} />
    );
  } else if (rawChannels.length === 0) {
    listBody = emptyStateContent ?? (
      <EmptyState
        icon={<AlertRegular />}
        title="No Notification Channels"
        description="No notification channels configured. Add a channel to receive alerts."
        actionLabel={onAddChannel ? 'Add Channel' : undefined}
        onAction={onAddChannel}
      />
    );
  } else {
    listBody = (
      <ListLoadingOverlay busy={isFetching && !isLoading}>
        <Table aria-label="Notification channels">
          <TableHeader>
            <TableRow>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'name' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('name')}
                onClick={() => toggleSort('name')}
                style={{ width: '100%' }}
              >
                Name
              </TableHeaderCell>
              <TableHeaderCell>Type</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Min Severity</TableHeaderCell>
              <TableHeaderCell>Stats</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortField === 'lastUsedAt' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={sortIcon('lastUsedAt')}
                onClick={() => toggleSort('lastUsedAt')}
              >
                Last Used
              </TableHeaderCell>
              <TableHeaderCell style={{ width: actionsColumnWidth(actionButtonCount) }}>
                Actions
              </TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedChannels.map((channel) => {
              const typeInfo = getChannelTypeInfo(channel.channelType);
              const isCurrentlyTesting = testingChannelId === channel.id;
              const currentTestResult = testResult?.id === channel.id ? testResult.result : null;

              return (
                <TableRow key={channel.id}>
                  <TableCell>
                    <div className={styles.channelTypeIcon}>
                      <Text weight="semibold">{channel.name}</Text>
                      {channel.isDefault && (
                        <Badge appearance="outline" size="medium">
                          Default
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge appearance="filled" color={typeInfo.color}>
                      {typeInfo.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      appearance="filled"
                      color={getStatusBadgeColor(channel.status)}
                      icon={channel.status === 'Active' ? <CheckmarkCircleRegular /> : channel.status === 'Error' ? <ErrorCircleRegular /> : undefined}
                    >
                      {channel.status}
                    </Badge>
                    {channel.lastError && channel.status === 'Error' && (
                      <Tooltip content={channel.lastError} relationship="description">
                        <ErrorCircleRegular style={{ marginLeft: '4px', color: tokens.colorPaletteRedForeground1 }} />
                      </Tooltip>
                    )}
                    {currentTestResult && (
                      <Badge
                        appearance="outline"
                        color={currentTestResult.success ? 'success' : 'danger'}
                        style={{ marginLeft: '8px' }}
                      >
                        {currentTestResult.success ? 'Test Passed' : 'Test Failed'}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge appearance="outline" color="subtle">
                      {channel.minimumSeverity}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className={styles.stats}>
                      <span className={styles.successCount}>
                        ✓ {channel.successCount}
                      </span>
                      <span className={styles.failureCount}>
                        ✗ {channel.failureCount}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Text size={200}>{formatDate(channel.lastUsedAt)}</Text>
                  </TableCell>
                  <TableCell>
                    <div className={styles.actionsCell}>
                      <Tooltip content="Test Channel" relationship="label">
                        <Button
                          appearance="subtle"
                          icon={isCurrentlyTesting ? <Spinner size="tiny" /> : <ArrowSyncRegular />}
                          size="small"
                          aria-label="Test Channel"
                          disabled={isCurrentlyTesting}
                          onClick={() => handleTestChannel(channel)}
                        />
                      </Tooltip>
                      {/* Hidden-not-disabled: Delete requires Organization.Admin / TenantAdmin /
                          IntegrationAdmin server-side — non-managers never see the button. */}
                      {canManageChannels && (
                        <Tooltip content="Delete Channel" relationship="label">
                          <Button
                            appearance="subtle"
                            icon={<DeleteRegular />}
                            size="small"
                            aria-label="Delete Channel"
                            onClick={() => setChannelToDelete(channel)}
                          />
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  return (
    <>
      <Card className={styles.card}>
        <CardHeader
          image={<AlertRegular />}
          header={
            <Text weight="semibold">
              Notification Channels{!isLoading && !isError ? ` (${totalCount})` : ''}
            </Text>
          }
          action={
            <div className={styles.headerActions}>
              {onAddChannel && (
                <Button appearance="primary" size="small" onClick={onAddChannel}>
                  Add Channel
                </Button>
              )}
            </div>
          }
        />
        <div className={styles.listSection}>
          <ListFrame>{listBody}</ListFrame>
          {!isLoading && !isError && rawChannels.length > 0 && (
            <Pager
              page={currentPage}
              totalPages={totalPages}
              hasPreviousPage={hasPreviousPage}
              hasNextPage={hasNextPage}
              totalCount={totalCount}
              onPageChange={setPage}
              testId="notification-channels-pager"
            />
          )}
        </div>
      </Card>

      {/* Delete Channel Confirmation Dialog */}
      <Dialog
        open={!!channelToDelete}
        onOpenChange={(_, data) => !data.open && setChannelToDelete(null)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete Notification Channel</DialogTitle>
            <DialogContent>
              <Text>
                Are you sure you want to delete the channel{' '}
                <Text weight="semibold">{channelToDelete?.name}</Text>?
                You will no longer receive notifications through this channel.
              </Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setChannelToDelete(null)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleDeleteChannel}
                disabled={deleteChannel.isPending}
              >
                {deleteChannel.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};
